import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import {
  ACCOUNT_IFSC_CODE,
  DEFAULT_SAVINGS_BANK_FOR_USER,
  DEFAULT_ACCOUNT_TYPE_FOR_USER,
  BANK_ACCOUNT_LABELS,
} from '@/constants.js';
import BankDetailField from './BankDetailsField.jsx';
import Spinner from '@/components/units/Spinner';
import featureFlags from '../../../featureflag.json';
import QRCodeContainer from './QRCodeContainer.jsx';
import EarnInterestBanner from '../../banners/EarnInterestBanner.jsx';
import InvestInterestBanner from '../../banners/InvestInterestBanner.jsx';

import { baseFormat } from '@/utility/formatter';

import {
  setBankLoadingAction,
  setBankDataAction,
  setEmptyBankDataAction,
  setBankErrorAction,
} from "@/store/bank/bank";
import { getUserBankDetails } from "@/services/rest/bank-info";

const BankPage = () => {
  const [showQRCodePopUp, setShowQRCodePopUp] = useState(true);
  
  const userData = useSelector(state => state.user.user)
  const isBankLoading = useSelector(state => state.bank.isLoading)
  const bankData = useSelector(state => state.bank)
  const bankAccount = useSelector(state => state.bank.bankAccounts?.[0] || {})

  const dispatch = useDispatch()

  const fetchBankDetails = async () => {
    dispatch(setBankLoadingAction(true));
    try {
      const userBankDetails = await getUserBankDetails()
      dispatch(setBankDataAction(userBankDetails));
    } catch (error) {
      dispatch(setBankErrorAction(error.message));
    }
  }

  useEffect(()=>{
    const hasSSFCAccount = !!userData?.ssfbCustomerId
    if (hasSSFCAccount && !bankAccount.bankAccountNumber) {
      // TODO: Load only if account detail not available
      fetchBankDetails()
    } else {
      dispatch(setEmptyBankDataAction());
    }
  },[userData])

  const showDownloadAppScanner = featureFlags?.appStoreQR?.showQRCode;

  if(isBankLoading){
    return (
      <div className="tw-text-center tw-py-[120px]">
        <Spinner />
      </div>
    )
  }

  const bankAccountRow1Fields = [
    { label: BANK_ACCOUNT_LABELS.SAVING_ACCOUNT, value: `By ${DEFAULT_SAVINGS_BANK_FOR_USER}` },
    {
      label: BANK_ACCOUNT_LABELS.TOTAL_BALANCE,
      value: baseFormat(bankAccount.balance)
    },
    {
      label: BANK_ACCOUNT_LABELS.NET_AVAILABLE_BALANCE,
      value: baseFormat(bankAccount.availableBalance)
    },
    { label: BANK_ACCOUNT_LABELS.BLOCKED_AMOUNT, value: baseFormat(bankAccount.blockedAmount) },
  ];
  const bankAccountRow2Fields = [
    {
      label: BANK_ACCOUNT_LABELS.ACCOUNT_HOLDER_NAME,
      value: bankData.customer ? 
        (`${bankData.customer.title || ''} 
        ${bankData.customer.firstName || ''} 
        ${bankData.customer.middleName || ''} 
        ${bankData.customer.lastName || ''}`).replace("/  /g"," ") : 'NA',
    },
    { label: BANK_ACCOUNT_LABELS.ACCOUNT_NUMBER, value: bankAccount.bankAccountNumber },
    { label: BANK_ACCOUNT_LABELS.ACCOUNT_TYPE, value: DEFAULT_ACCOUNT_TYPE_FOR_USER },
    { label: BANK_ACCOUNT_LABELS.IFSC_CODE, value: ACCOUNT_IFSC_CODE },
  ]

  function handleCloseQRCodePopUp() {
    setShowQRCodePopUp(false);
  }

  return (
    <>
      <div
        id="bank-details-page"
        className="tw-flex tw-items-start tw-justify-center tw-px-4 md:tw-px-6 lg:tw-px-8 tw-pt-4 tw-pb-20 md:tw-py-10"
      >
        <div className="tw-max-w-screen-lg tw-w-full">
          <div className="tw-text-xl md:tw-text-2xl lg:tw-text-[1.5rem] tw-font-normal">
            Digital Bank
          </div>

          <div id="bank-details-container" className="tw-w-full tw-py-2 md:tw-py-4">
            <div className="tw-p-4 md:tw-p-6 tw-flex tw-flex-col tw-items-stretch tw-gap-4 tw-bg-white tw-border tw-border-solid tw-border-[#EBEBEB] tw-rounded-lg">
              <div className='tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4 tw-opacity-100 tw-text-base'>
                {bankAccountRow1Fields.map((field,index) => (
                  <BankDetailField key={field.label} label={field.label} value={field.value} labelClassName={index==0?'!tw-opacity-100 tw-text-[#001316] !tw-text-base':''} valueClassName={index==0?'tw-text-sm':''} />
                ))}
              </div>
              <div className='tw-w-full tw-h-0 tw-border-t tw-border-solid tw-border-[#F2F2F2]'></div> 
              <div className='tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4'>
                {bankAccountRow2Fields.map((field) => (
                  <BankDetailField key={field.label} label={field.label} value={field.value} />
                ))}
              </div>
            </div>

            <div className="tw-p-4 md:tw-p-6 lg:tw-p-8 tw-mt-4 tw-bg-white tw-border tw-border-solid tw-border-[#EBEBEB] tw-rounded-lg">
              <div className='tw-flex tw-flex-col tw-gap-4'>
                <div className="tw-text-lg md:tw-text-2xl tw-text-center">
                  Use our mobile app to avail these benefits!
                </div>
                {featureFlags?.banners?.bankPage?.earnInterest && <EarnInterestBanner />}
                {featureFlags?.banners?.bankPage?.investInterest && <InvestInterestBanner />}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showDownloadAppScanner && showQRCodePopUp ? (
        <QRCodeContainer onClosehandler={handleCloseQRCodePopUp} />
      ) : null}
    </>
  );
};

export default BankPage;
